/**
 * Phone number validation utilities — mirrors PhoneUtil.java on the backend.
 *
 * Currency → country phone format mapping:
 *   INR  India         10 digits, starts with 6-9          e.g. 9876543210
 *   USD  USA / Canada  10-digit NANP                        e.g. 2125551234
 *   GBP  UK            10-11 digits, starts with 07 / +44  e.g. 07911123456
 *   AED  UAE           9 digits, starts with 05X / +971     e.g. 0501234567
 *   BDT  Bangladesh    11 digits, starts with 01 / +880     e.g. 01812345678
 *   EUR  EU            generic E.164 (too varied per country)
 */

const PHONE_CONFIGS = {
  INR: {
    regex: /^(\+91[\s-]?)?[6-9]\d{9}$/,
    placeholder: '9876543210',
    hint: '10-digit Indian mobile (e.g. 9876543210)',
  },
  USD: {
    regex: /^(\+1[\s-]?)?[2-9]\d{9}$/,
    placeholder: '2125551234',
    hint: '10-digit US/Canada number (e.g. 2125551234)',
  },
  GBP: {
    regex: /^(\+44[\s-]?|0)[1-9]\d{8,9}$/,
    placeholder: '07911123456',
    hint: '10-11 digit UK number (e.g. 07911123456)',
  },
  AED: {
    regex: /^(\+971[\s-]?|0)[5-9]\d{8}$/,
    placeholder: '0501234567',
    hint: '9-digit UAE mobile (e.g. 0501234567)',
  },
  BDT: {
    regex: /^(\+880[\s-]?|0)1[3-9]\d{8}$/,
    placeholder: '01812345678',
    hint: '11-digit Bangladeshi number (e.g. 01812345678)',
  },
};

const DEFAULT_CONFIG = {
  regex: /^\+?[1-9]\d{6,14}$/,
  placeholder: '+12125551234',
  hint: 'Valid international phone number',
};

/**
 * Returns the phone config (regex, placeholder, hint) for the given currency.
 * Falls back to generic E.164 for unrecognised currencies.
 */
export function getPhoneConfig(currency) {
  return PHONE_CONFIGS[currency?.toUpperCase()] || DEFAULT_CONFIG;
}

/**
 * Validates a phone number string against the currency's expected format.
 * Returns null if valid, or an error message string if invalid.
 * Empty / null values return null (valid) — use required checks separately.
 */
export function validatePhone(phone, currency) {
  if (!phone || !phone.trim()) return null;
  const cleaned = phone.replace(/[\s\-().]+/g, '');
  const config = getPhoneConfig(currency);
  return config.regex.test(cleaned) ? null : `Invalid phone. Expected: ${config.hint}`;
}
